import React, { useState, useEffect, useCallback } from 'react';
import listBuildingService, { ListType, ListBuildingGuide } from '../services/listBuildingService';
import gtmService, { Icp, Persona } from '../services/gtmService';
import MarkdownView from '../components/MarkdownView';
import SavedAiDocuments from '../components/SavedAiDocuments';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';

const LIST_TYPE_OPTIONS: { value: ListType; label: string }[] = [
  { value: 'both', label: 'Beides (erst Firmen, dann Kontakte)' },
  { value: 'companies', label: 'Firmen / Accounts' },
  { value: 'contacts', label: 'Kontakte / Personen' },
];

const ListBuildingPage: React.FC = () => {
  const [listType, setListType] = useState<ListType>('both');
  const [icpId, setIcpId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [product, setProduct] = useState('');
  const [context, setContext] = useState('');
  const [icps, setIcps] = useState<Icp[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guide, setGuide] = useState<ListBuildingGuide | null>(null);
  const [raw, setRaw] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const loadGtm = useCallback(async () => {
    try {
      const [i, p] = await Promise.all([gtmService.getIcps(), gtmService.getPersonas()]);
      setIcps(i);
      setPersonas(p);
    } catch {
      // GTM context is optional.
    }
  }, []);

  useEffect(() => {
    loadGtm();
  }, [loadGtm]);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError('');
      setGuide(null);
      setRaw('');
      const result = await listBuildingService.generateGuide({
        listType,
        icpId: icpId || undefined,
        personaId: personaId || undefined,
        product: product || undefined,
        context: context || undefined,
      });
      if (result.guide) setGuide(result.guide);
      else setRaw(result.raw || 'Kein strukturierter Guide erhalten.');
      setReloadKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Erstellen des Guides');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <PageHeader title="Zielfirmenlisten (Listbuilding)" subtitle={routeById('list-building')?.description} />
      <p style={{ color: 'var(--sec-fg-muted)', marginBottom: '24px' }}>
        Übersetzt einen ICP/eine Persona in eine konkrete, schrittweise lemlist-Such-Konfiguration
        (powered by den Skills <em>list-builder</em>, <em>people-finder</em>, <em>company-finder</em>) —
        lemlist-native, mit Signalen und dem Prinzip „enge Liste schlägt große".
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="form-section" style={{ borderLeft: '4px solid var(--sec-accent)' }}>
        <div className="form-group">
          <label className="form-label">Listentyp</label>
          <select className="form-input" value={listType} onChange={(e) => setListType(e.target.value as ListType)}>
            {LIST_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 220px' }}>
            <label className="form-label">ICP (GTM, optional)</label>
            <select className="form-input" value={icpId} onChange={(e) => setIcpId(e.target.value)}>
              <option value="">— keiner —</option>
              {icps.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: '1 1 220px' }}>
            <label className="form-label">Persona (GTM, optional)</label>
            <select className="form-input" value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
              <option value="">— keine —</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Produkt (Freitext, falls kein GTM)</label>
          <input className="form-input" value={product} onChange={(e) => setProduct(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Zusätzlicher Kontext (optional)</label>
          <textarea className="form-textarea" rows={2} value={context} onChange={(e) => setContext(e.target.value)} />
        </div>
        <button className="btn btn-success" onClick={handleGenerate} disabled={loading}>
          {loading ? 'Erstelle Guide…' : 'Guide erstellen'}
        </button>
      </div>

      {raw && <MarkdownView content={raw} style={{ marginTop: '16px' }} />}

      {guide && (
        <div style={{ marginTop: '20px' }}>
          {guide.summary && <div className="alert alert-success">{guide.summary}</div>}

          {guide.steps.map((s, i) => (
            <div className="card" key={i} style={{ marginBottom: '12px' }}>
              <div className="card-title">
                {i + 1}. {s.category}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)', marginTop: '6px' }}>
                {s.what && (
                  <div>
                    <strong>Was:</strong> {s.what}
                  </div>
                )}
                {s.why && (
                  <div>
                    <strong>Warum:</strong> {s.why}
                  </div>
                )}
                {s.recommendation && (
                  <div style={{ color: 'var(--sec-fg)', marginTop: '4px' }}>
                    <strong>Empfehlung:</strong> {s.recommendation}
                  </div>
                )}
              </div>
            </div>
          ))}

          {guide.titleVariations?.length > 0 && (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="card-title" style={{ marginBottom: '6px' }}>Titel-Varianten</div>
              <div style={{ fontSize: '13px', color: 'var(--sec-fg)' }}>{guide.titleVariations.join(' · ')}</div>
            </div>
          )}

          {guide.signals?.length > 0 && (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="card-title" style={{ marginBottom: '6px' }}>Signale aktivieren</div>
              <ul style={{ margin: '0 0 0 18px', fontSize: '13px', color: 'var(--sec-fg)' }}>
                {guide.signals.map((sig, i) => (
                  <li key={i}>{sig}</li>
                ))}
              </ul>
            </div>
          )}

          {guide.targetListSize && (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div className="card-title" style={{ marginBottom: '6px' }}>Empfohlene Listengröße</div>
              <div style={{ fontSize: '13px', color: 'var(--sec-fg)' }}>{guide.targetListSize}</div>
            </div>
          )}

          {guide.notes && (
            <div style={{ fontSize: '13px', color: 'var(--sec-fg-muted)' }}>{guide.notes}</div>
          )}
        </div>
      )}

      <SavedAiDocuments feature="list_building" reloadKey={reloadKey} />
    </div>
  );
};

export default ListBuildingPage;
